import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    TicketIcon, 
    CheckCircleIcon, 
    RocketLaunchIcon, 
    SparklesIcon, 
    XMarkIcon 
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase'; // Link with Al-Baron Firebase
import { 
    collection, query, where, getDocs, updateDoc, 
    doc, serverTimestamp, onSnapshot, runTransaction 
} from 'firebase/firestore';
import { signOut } from "firebase/auth"; // 🛡️ استيراد دالة الطرد للحماية (Baron Security)

// 🚀 [SECTION: PROPS_LOGIC] - تم إضافة isPopup و onClose لدعم العرض المنبثق فوق طبقة الحماية
const ActivateCode = ({ isPopup = false, onClose }) => {
    const navigate = useNavigate();
    const [code, setCode] = useState("");
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [userSubStatus, setUserSubStatus] = useState(""); // [حالة اشتراك الطالب الحالية]

    // 🛠️ [FORMATTER_LOGIC] - اللمسة البارونية لثبات التقسيم على (4-4-3)
    const handleCodeChange = (e) => {
        let val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
        let formatted = "";
        
        if (val.length > 0) {
            formatted = val.substring(0, 4); // أول 4
            if (val.length > 4) formatted += "-" + val.substring(4, 8); // ثاني 4
            if (val.length > 8) formatted += "-" + val.substring(8, 11); // آخر 3 فقط
        }
        setCode(formatted);
    };

    // 🛡️ [MONITOR_USER_STATUS] - مراقبة حالة الطالب لتقديم التوجيه الصحيح في Placeholder
    useEffect(() => {
        if (auth.currentUser) {
            const unsub = onSnapshot(doc(db, "users", auth.currentUser.uid), (docSnap) => {
                if (docSnap.exists()) {
                    setUserSubStatus(docSnap.data().activeCourse || "");
                }
            });
            return () => unsub();
        }
    }, []);

    // 🚀 [FUNCTION: ACTIVATE_LOGIC] - دالة التفعيل الذكية والمحصنة بدمج الكورسات الملكي (Al-Baron Logic)
    const handleActivate = async () => {
        // تنظيف المدخلات (حذف الشرطات وكل الرموز غير النصية) للمطابقة مع القاعدة
        const cleanInput = code.replace(/[^A-Z0-9]/g, '').trim().toUpperCase();

        if (!cleanInput) {
            setError("يرجى إدخال الكود أولاً");
            return;
        }

        setLoading(true);
        setError("");

        try {
            // 1. البحث بذكاء: جلب الأكواد المتاحة ومطابقتها مع المدخل المنظف
            const tokensRef = collection(db, "tokens");
            const q = query(tokensRef, where("status", "==", "available"));
            const querySnapshot = await getDocs(q);
            
            const tokenDoc = querySnapshot.docs.find(doc => 
                doc.data().code.toUpperCase().replace(/[-\s]/g, '') === cleanInput
            );

            // 🛡️ [AL-BARON_SECURITY_CHECK]: إذا الكود خطأ، يتم طرد المستخدم فوراً لحماية المنصة
            if (!tokenDoc) {
                setError("الكود غير صالح! تم تسجيل خروجك للأمان ❌");
                setTimeout(async () => {
                    await signOut(auth);
                    navigate('/login');
                }, 2000);
                setLoading(false);
                return;
            }

            const tokenData = tokenDoc.data();
            const duration = tokenData.duration || ""; // (كورس أول، كورس ثاني، كورسين كامل)

            // 🛠️ [AL-BARON_FIX]: تنظيف الهمزات لضمان مطابقة النصوص بدقة
            const cleanDuration = duration.replace(/[أإآ]/g, "ا").toLowerCase();

            // 🎯 [SMART_TRANSACTION_MERGE]: معالجة دمج الكورسات الملكية بدون مسح الصلاحيات القديمة
            await runTransaction(db, async (transaction) => {
                const userRef = doc(db, "users", auth.currentUser.uid);
                const tokenRef = doc(db, "tokens", tokenDoc.id);

                // قراءة بيانات الطالب الحالية من الفايربيس قبل التحديث
                const userSnap = await transaction.get(userRef);
                const currentUserData = userSnap.exists() ? userSnap.data() : {};

                // فحص الاشتراكات السابقة للطالب وإبقاء المكسوب منها
                let isC1 = currentUserData.isCourse1Active === true;
                let isC2 = currentUserData.isCourse2Active === true;

                // تحديث الصلاحيات بناءً على التوكن الجديد مع الحفاظ على القديم
                if (cleanDuration.includes("اول") || cleanDuration.includes("1")) isC1 = true;
                if (cleanDuration.includes("ثاني") || cleanDuration.includes("2")) isC2 = true;
                if (cleanDuration.includes("كامل") || cleanDuration.includes("كورسين")) {
                    isC1 = true;
                    isC2 = true;
                }

                // إعداد بيانات التحديث الذكية
                let updateData = {
                    isActivated: true,
                    isSubscribed: true,
                    subscriptionDate: serverTimestamp(),
                    isCourse1Active: isC1,
                    isCourse2Active: isC2,
                    usedToken: tokenData.code
                };

                // 🎯 إذا أصبح الطالب يملك الكورسين معاً، يتم تحويل اسم اشتراكه فوراً إلى "المنهج كامل"
                if (isC1 && isC2) {
                    updateData.activeCourse = "المنهج كامل";
                    updateData.subscriptionType = "المنهج كامل";
                } else {
                    updateData.activeCourse = duration;
                    updateData.subscriptionType = duration;
                }

                // تحديث وثيقة الطالب ووثيقة الكود دفعة واحدة وبشكل آمن 100%
                transaction.update(userRef, updateData);
                transaction.update(tokenRef, {
                    status: "sold",
                    activatedBy: auth.currentUser.email || "",
                    activatedAt: serverTimestamp(),
                    usedBy: auth.currentUser.uid
                });
            });

            // 🎉 [SUCCESS_STATE] - إظهار نافذة النجاح الملكية
            setIsSuccess(true);
            
        } catch (err) {
            console.error(err);
            setError("حدث خطأ في الاتصال 🌐");
        } finally {
            setLoading(false);
        }
    };

    // 🧱 [BARON_LAYOUT]: تم تقليل المسافات (Spacing) لتبدو البطاقة مربعة وأصغر
    const containerClasses = isPopup 
        ? "w-full space-y-5 font-cairo" 
        : "royal-smoked-glass min-h-screen w-full flex items-center justify-center p-4 font-cairo"; 

    return (
        <div className={containerClasses} dir="rtl">
            
            {/* 1️⃣ [MAIN_INPUT_INTERFACE] - تم تصغير الحشوة (Padding) والحدود (Rounded) لتصبح مربعة أكثر */}
            <motion.div 
                initial={isPopup ? {} : { opacity: 0, scale: 0.9 }} 
                animate={isPopup ? {} : { opacity: 1, scale: 1 }} 
                className={isPopup ? "space-y-5" : "ios-glass p-6 md:p-8 w-full max-w-[340px] rounded-[35px] border-white/40 shadow-2xl text-center space-y-5 bg-white/90 dark:bg-[#1C1C1E]/95"}
            >
                <div className="w-16 h-16 bg-[#007AFF]/10 rounded-[22px] flex items-center justify-center mx-auto shadow-inner relative">
                    <TicketIcon className="w-8 h-8 text-[#007AFF]" />
                    {isPopup && onClose && (
                        <button onClick={onClose} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full shadow-lg opacity-0 pointer-events-none">
                            <XMarkIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>

                <div>
                    {/* [SMART_TITLE]: حجم خط أصغر (16px) ليتناسب مع البطاقة المربعة */}
                    <h2 className="text-[16px] font-black text-gray-800 dark:text-white tracking-tight font-cairo whitespace-nowrap">
                        {userSubStatus.includes("أول") ? "تفعيل الكورس الثاني" : 
                         userSubStatus.includes("ثاني") ? "تفعيل الكورس الأول" : 
                         "تفعيل الاشتراك | Activation"}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400 font-bold mt-1 text-[11px] font-cairo px-2">
                        أدخل الرمز السري - لفتح المحاضرات 🚀
                    </p>
                </div>

                <div className="relative group">
                    <input 
                        type="text" 
                        value={code} 
                        onChange={handleCodeChange}
                        placeholder="XXXX-XXXX-XXX"
                        maxLength="13" // 🟢 هذا القيد يمنع الزيادة
                        disabled={loading}
                        className={`w-full bg-white/60 dark:bg-black/20 border-2 ${error ? 'border-red-400' : 'border-gray-100 dark:border-white/10'} focus:border-[#007AFF] p-3 rounded-[18px] text-center text-sm font-black tracking-widest outline-none transition-all placeholder:tracking-normal placeholder:text-gray-300 shadow-inner font-cairo dark:text-white`}
                    />
                    {error && (
                        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-500 text-[10px] font-black mt-2 bg-red-50 dark:bg-red-900/20 py-1.5 rounded-lg border border-red-100 dark:border-red-900/30 font-cairo">
                            {error}
                        </motion.p>
                    )}
                </div>

                <button 
                    onClick={handleActivate}
                    disabled={loading}
                    className="w-full bg-[#007AFF] text-white py-3 rounded-[18px] font-black text-sm shadow-lg shadow-blue-500/20 active:scale-95 transition-all flex items-center justify-center gap-2 group disabled:bg-gray-400 font-cairo"
                >
                    {loading ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                        <>
                            <RocketLaunchIcon className="w-4 h-4" />
                            تفعيل الآن ✅
                        </>
                    )}
                </button>
            </motion.div>

            {/* 2️⃣ [SUCCESS_OVERLAY] - نافذة النجاح الملكية المصغرة */}
            <AnimatePresence>
                {isSuccess && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[10000000] flex items-center justify-center bg-black/60 backdrop-blur-xl p-4 font-cairo"
                    >
                        <motion.div 
                            initial={{ scale: 0.8, y: 30 }}
                            animate={{ scale: 1, y: 0 }}
                            className="bg-white dark:bg-[#1C1C1E] rounded-[35px] p-6 max-w-[280px] w-full text-center shadow-2xl border border-white dark:border-white/10 relative overflow-hidden font-cairo"
                        >
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircleIcon className="w-10 h-10 text-green-500" />
                            </div>
                            
                            <h3 className="text-lg font-black text-gray-800 dark:text-white font-cairo">تم التفعيل! 🎉</h3>
                            <p className="text-gray-500 dark:text-gray-400 font-bold mt-1 mb-5 text-[12px] leading-relaxed font-cairo">
                                انطلق يا بطل، المحاضرات أصبحت متاحة لك الآن بالكامل.
                            </p>
                            
                            <button 
                                onClick={() => {
                                    if(isPopup) {
                                        window.location.reload();
                                    } else {
                                        navigate('/student/courses');
                                    }
                                }}
                                className="w-full bg-[#007AFF] text-white py-3 rounded-[18px] font-black text-sm active:scale-95 flex items-center justify-center gap-2 shadow-md font-cairo"
                            >
                                <SparklesIcon className="w-4 h-4 text-orange-400" />
                                ابدأ الآن 🚀
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ActivateCode;